<template>
  <v-dialog
    v-model="localShow"
    max-width="700px"
    scrollable
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card class="comments-dialog">
      <!-- Header del diálogo -->
      <v-card-title class="d-flex align-center pa-4">
        <v-icon
          icon="mdi-comment-multiple"
          size="24"
          class="mr-3"
        ></v-icon>
        <div class="flex-grow-1">
          <h2 class="text-h6 mb-1">
            Comentarios de Incidencia
          </h2>
          <p class="text-caption text-grey mb-0">
            #{{ incident?.id }} - {{ incident?.title }}
          </p>
        </div>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          @click="handleClose"
        ></v-btn>
      </v-card-title>

      <!-- Lista de comentarios -->
      <v-card-text class="pa-0" style="max-height: 500px;">
        <div class="comments-container">
          <!-- Información de la incidencia -->
          <v-alert
            type="info"
            variant="tonal"
            class="ma-4 mb-4"
          >
            <div class="incident-info">
              <div class="incident-title">
                <strong>{{ incident?.title }}</strong>
              </div>
              <div class="incident-meta">
                <v-chip
                  :color="getPriorityColor(incident?.priority || 'MEDIUM')"
                  size="small"
                  variant="tonal"
                  class="mr-2"
                >
                  {{ getPriorityText(incident?.priority || 'MEDIUM') }}
                </v-chip>
                <v-chip
                  :color="getStatusColor(incident?.status || 'PENDING')"
                  size="small"
                  variant="tonal"
                >
                  {{ getStatusText(incident?.status || 'PENDING') }}
                </v-chip>
              </div>
            </div>
          </v-alert>

          <!-- Lista de comentarios -->
          <div class="comments-list pa-4">
            <!-- Loading state -->
            <div v-if="loading" class="text-center py-8">
              <v-progress-circular
                indeterminate
                color="primary"
                class="mb-4"
              ></v-progress-circular>
              <p class="text-grey">Cargando comentarios...</p>
            </div>

            <!-- Error state -->
            <v-alert
              v-else-if="error"
              type="error"
              variant="tonal"
              class="ma-4"
              closable
              @click:close="error = null"
            >
              {{ error }}
            </v-alert>

            <!-- Empty state -->
            <div v-else-if="comments.length === 0" class="no-comments">
              <div class="text-center text-grey py-8">
                <v-icon size="48" class="mb-4">mdi-comment-off</v-icon>
                <h3>No hay comentarios</h3>
                <p>Sé el primero en agregar un comentario a esta incidencia.</p>
              </div>
            </div>
            
            <div v-else class="comments-timeline">
              <v-timeline density="compact" side="end">
                <v-timeline-item
                  v-for="comment in sortedComments"
                  :key="comment.id"
                  :dot-color="comment.isDeleted ? 'grey' : 'primary'"
                  size="small"
                >
                  <div class="comment-card">
                    <v-card 
                      :elevation="comment.isDeleted ? 0 : 2" 
                      :class="['comment-item', { 'deleted-comment': comment.isDeleted }]"
                    >
                      <v-card-text class="pa-4">
                        <!-- Comentario al que se responde (si existe) -->
                        <div v-if="comment.replyTo" class="reply-to-preview mb-2">
                          <v-icon icon="mdi-reply" size="14" class="mr-1"></v-icon>
                          <span class="text-caption">
                            <strong>{{ comment.replyTo.userName }}</strong>: 
                            {{ comment.replyTo.comment }}
                          </span>
                        </div>

                        <div class="comment-header">
                          <div class="comment-author">
                            <v-avatar size="32" class="mr-3">
                              <v-icon :icon="comment.isDeleted ? 'mdi-account-off' : 'mdi-account'" size="18"></v-icon>
                            </v-avatar>
                            <div>
                              <div class="author-name">{{ comment.userName }}</div>
                              <div class="comment-time">
                                {{ formatDate(comment.timestamp) }}
                                <span v-if="comment.isEdited && !comment.isDeleted" class="edited-badge ml-1">
                                  (editado)
                                </span>
                              </div>
                            </div>
                          </div>
                          <v-menu v-if="canModifyComment(comment)">
                            <template v-slot:activator="{ props }">
                              <v-btn
                                icon="mdi-dots-vertical"
                                variant="text"
                                size="small"
                                v-bind="props"
                              ></v-btn>
                            </template>
                            <v-list>
                              <v-list-item 
                                v-if="!comment.isDeleted && canEditComment(comment)"
                                @click="startEditComment(comment)"
                              >
                                <template v-slot:prepend>
                                  <v-icon icon="mdi-pencil" color="primary"></v-icon>
                                </template>
                                <v-list-item-title>Editar</v-list-item-title>
                              </v-list-item>
                              <v-list-item 
                                v-if="!comment.isDeleted"
                                @click="startReplyComment(comment)"
                              >
                                <template v-slot:prepend>
                                  <v-icon icon="mdi-reply" color="info"></v-icon>
                                </template>
                                <v-list-item-title>Responder</v-list-item-title>
                              </v-list-item>
                              <v-list-item 
                                v-if="canDeleteComment(comment)"
                                @click="deleteComment(comment.id)"
                              >
                                <template v-slot:prepend>
                                  <v-icon icon="mdi-delete" color="error"></v-icon>
                                </template>
                                <v-list-item-title>Eliminar</v-list-item-title>
                              </v-list-item>
                            </v-list>
                          </v-menu>
                        </div>
                        
                        <div :class="['comment-content', { 'deleted-text': comment.isDeleted }]">
                          {{ comment.comment }}
                        </div>
                      </v-card-text>
                    </v-card>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </div>
          </div>
        </div>
      </v-card-text>

      <!-- Formulario para nuevo comentario -->
      <v-card-actions class="comment-form-container pa-4 pt-0">
        <div class="w-100">
          <!-- Banner de respuesta/edición -->
          <v-alert
            v-if="replyingTo || editingComment"
            :type="editingComment ? 'info' : 'success'"
            variant="tonal"
            closable
            class="mb-3"
            @click:close="cancelAction"
          >
            <div class="d-flex align-center">
              <v-icon 
                :icon="editingComment ? 'mdi-pencil' : 'mdi-reply'" 
                class="mr-2"
              ></v-icon>
              <div>
                <div class="text-subtitle-2">
                  {{ editingComment ? 'Editando comentario' : `Respondiendo a ${replyingTo?.userName}` }}
                </div>
                <div class="text-caption text-grey">
                  {{ editingComment ? editingComment.comment.substring(0, 80) : replyingTo?.comment.substring(0, 80) }}...
                </div>
              </div>
            </div>
          </v-alert>

          <v-form ref="formRef" v-model="formValid" @submit.prevent="handleSubmit">
            <v-textarea
              v-model="newComment"
              :label="editingComment ? 'Editar comentario' : 'Agregar comentario'"
              variant="outlined"
              rows="3"
              counter="500"
              maxlength="500"
              :rules="commentRules"
              :prepend-inner-icon="editingComment ? 'mdi-pencil' : 'mdi-comment-edit'"
              placeholder="Escribe tu comentario aquí..."
              class="mb-3"
            ></v-textarea>
            
            <div class="d-flex justify-space-between align-center">
              <div class="comment-tips">
                <v-icon icon="mdi-information" size="16" class="mr-1"></v-icon>
                <span class="text-caption text-grey">
                  Los comentarios quedan registrados permanentemente
                </span>
              </div>
              
              <div class="comment-actions">
                <v-btn
                  v-if="replyingTo || editingComment"
                  variant="text"
                  @click="cancelAction"
                  :disabled="adding"
                  class="mr-2"
                >
                  Cancelar
                </v-btn>
                <v-btn
                  variant="outlined"
                  @click="handleClose"
                  :disabled="adding"
                  class="mr-2"
                >
                  Cerrar
                </v-btn>
                <v-btn
                  color="primary"
                  variant="elevated"
                  @click="handleSubmit"
                  :loading="adding"
                  :disabled="!formValid || !newComment.trim()"
                  :prepend-icon="editingComment ? 'mdi-check' : 'mdi-send'"
                >
                  {{ editingComment ? 'Guardar' : (replyingTo ? 'Responder' : 'Agregar') }}
                </v-btn>
              </div>
            </div>
          </v-form>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useAuthStore } from '@/stores/auth'
// W2 LIFECYCLE-SIBLING (client truth): comment EDIT/DELETE are contained to vendor/conditional.
import { canUseLifecycleSiblingWrites } from '@/config/lifecycleSiblingPerimeter'
import websocketService from '@/services/websocket.service'
import { RT_LOCAL } from '@/services/realtime-events'
import { getApiOriginUrl } from '@/config/runtimeConfig'
import type { Incident, Comment } from '../../../types/incidents'

// Store
const auth = useAuthStore()

interface Props {
  modelValue: boolean
  incident?: Incident | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  close: []
}>()

// Referencias
const formRef = ref()
const formValid = ref(false)
const adding = ref(false)
const loading = ref(false)
const error = ref<string | null>(null)

// Estado del formulario
const newComment = ref('')
const replyingTo = ref<Comment | null>(null)
const editingComment = ref<Comment | null>(null)

// Estado de comentarios (simulado)
const comments = ref<Comment[]>([])

// Computed
const localShow = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const sortedComments = computed(() => {
  return [...comments.value].sort((a, b) => 
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
  )
})

// Reglas de validación
const commentRules = [
  (v: string) => !!v?.trim() || 'El comentario es requerido',
  (v: string) => (v?.trim()?.length >= 10) || 'El comentario debe tener al menos 10 caracteres',
  (v: string) => (v?.length <= 500) || 'El comentario no puede exceder 500 caracteres'
]

// Métodos
const loadComments = async () => {
  if (!props.incident) return
  
  loading.value = true
  error.value = null
  
  try {
    const apiUrl = getApiOriginUrl()
    const response = await fetch(
      `${apiUrl}/api/incidences/${props.incident.id}/comments`,
      {
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        }
      }
    )
    
    if (!response.ok) {
      throw new Error(`Error al cargar comentarios: ${response.status}`)
    }
    
    const result = await response.json()
    
    // La API devuelve { success: true, data: [...], count: n }
    if (result.success && Array.isArray(result.data)) {
      comments.value = result.data
      console.log(`✅ Cargados ${result.count} comentarios para incidencia #${props.incident.id}`)
      
      // DEBUG: Verificar estructura de fechas
      if (result.data.length > 0) {
        const firstComment = result.data[0]
        console.log('🔍 DEBUG - Primer comentario:', {
          id: firstComment.id,
          timestamp: firstComment.timestamp,
          createdAt: firstComment.createdAt,
          updatedAt: firstComment.updatedAt,
          timestampType: typeof firstComment.timestamp,
          createdAtType: typeof firstComment.createdAt
        })
      }
    } else {
      comments.value = []
      console.warn('⚠️ Respuesta inesperada del servidor:', result)
    }
  } catch (err: any) {
    console.error('❌ Error cargando comentarios:', err)
    error.value = err.message || 'Error al cargar comentarios'
    // Intentar usar comentarios del prop como fallback
    comments.value = props.incident.comments || []
  } finally {
    loading.value = false
  }
}

const handleAddComment = async () => {
  if (!formRef.value || !props.incident) return
  
  const { valid } = await formRef.value.validate()
  if (!valid || !newComment.value.trim()) return
  
  adding.value = true
  error.value = null
  
  try {
    const apiUrl = getApiOriginUrl()
    const response = await fetch(
      `${apiUrl}/api/incidences/${props.incident.id}/comments`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          comment: newComment.value.trim(),
          replyToId: replyingTo.value?.id || null
        })
      }
    )
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Error ${response.status}`)
    }
    
    const result = await response.json()
    
    if (result.success && result.data) {
      // Agregar el nuevo comentario a la lista
      comments.value.push(result.data)
      
      // NO emitir evento - el diálogo de detalles recargará automáticamente
      
      // Limpiar formulario
      cancelAction()
      
      console.log('✅ Comentario agregado exitosamente')
    } else {
      throw new Error('Respuesta inesperada del servidor')
    }
  } catch (err: any) {
    console.error('❌ Error agregando comentario:', err)
    error.value = err.message || 'Error al agregar comentario'
  } finally {
    adding.value = false
  }
}

const handleEditComment = async () => {
  // W2 LIFECYCLE-SIBLING: adversarial guard on the raw-fetch PUT dispatcher, independent of the
  // menu item and of `startEditComment`.
  if (!canUseLifecycleSiblingWrites(auth.user?.role)) return
  if (!formRef.value || !props.incident || !editingComment.value) return
  
  const { valid } = await formRef.value.validate()
  if (!valid || !newComment.value.trim()) return
  
  adding.value = true
  error.value = null
  
  try {
    const apiUrl = getApiOriginUrl()
    const response = await fetch(
      `${apiUrl}/api/incidences/${props.incident.id}/comments/${editingComment.value.id}`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          comment: newComment.value.trim()
        })
      }
    )
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Error ${response.status}`)
    }
    
    const result = await response.json()
    
    if (result.success && result.data) {
      // Actualizar el comentario en la lista
      const index = comments.value.findIndex(c => c.id === editingComment.value!.id)
      if (index !== -1) {
        comments.value[index] = result.data
      }
      
      // Limpiar formulario
      cancelAction()
      
      console.log('✅ Comentario editado exitosamente')
    } else {
      throw new Error('Respuesta inesperada del servidor')
    }
  } catch (err: any) {
    console.error('❌ Error editando comentario:', err)
    error.value = err.message || 'Error al editar comentario'
  } finally {
    adding.value = false
  }
}

const handleClose = () => {
  localShow.value = false
  emit('close')
}

const canDeleteComment = (comment: Comment) => {
  // W2 LIFECYCLE-SIBLING: the perimeter is checked FIRST, so the author/SuperAdmin rule below can
  // never re-admit a delivered identity to a capability the server now refuses.
  if (!canUseLifecycleSiblingWrites(auth.user?.role)) return false
  // No se pueden eliminar comentarios ya eliminados
  if (comment.isDeleted) return false
  // Solo permitir eliminar comentarios propios o si es SuperAdmin
  return comment.userId === auth.user?.id || auth.user?.role === 'super_admin'
}

const canEditComment = (comment: Comment) => {
  // W2 LIFECYCLE-SIBLING: same perimeter, checked before authorship.
  if (!canUseLifecycleSiblingWrites(auth.user?.role)) return false
  // Solo el autor puede editar su propio comentario
  return !comment.isDeleted && comment.userId === auth.user?.id
}

const canModifyComment = (comment: Comment) => {
  // Mostrar menú si puede eliminar, editar o responder
  return canDeleteComment(comment) || canEditComment(comment) || !comment.isDeleted
}

const startReplyComment = (comment: Comment) => {
  replyingTo.value = comment
  editingComment.value = null
  newComment.value = ''
  formRef.value?.resetValidation()
}

const startEditComment = (comment: Comment) => {
  editingComment.value = comment
  replyingTo.value = null
  newComment.value = comment.comment
  formRef.value?.resetValidation()
}

const cancelAction = () => {
  replyingTo.value = null
  editingComment.value = null
  newComment.value = ''
  formRef.value?.resetValidation()
}

const handleSubmit = async () => {
  if (editingComment.value) {
    await handleEditComment()
  } else {
    await handleAddComment()
  }
}

const deleteComment = async (commentId: string) => {
  // W2 LIFECYCLE-SIBLING: adversarial guard — this raw-fetch dispatcher is reachable independently
  // of the menu item that normally triggers it.
  if (!canUseLifecycleSiblingWrites(auth.user?.role)) return
  if (!props.incident) return
  
  if (!confirm('¿Estás seguro de que deseas eliminar este comentario?')) {
    return
  }
  
  try {
    const apiUrl = getApiOriginUrl()
    const response = await fetch(
      `${apiUrl}/api/incidences/${props.incident.id}/comments/${commentId}`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${auth.token}`,
          'Content-Type': 'application/json'
        }
      }
    )
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(errorData.error || `Error ${response.status}`)
    }
    
    const result = await response.json()
    
    if (result.success) {
      // Recargar comentarios para mostrar el mensaje "Se eliminó este mensaje"
      await loadComments()
      console.log('✅ Comentario eliminado exitosamente')
    } else {
      throw new Error('Respuesta inesperada del servidor')
    }
  } catch (err: any) {
    console.error('❌ Error eliminando comentario:', err)
    error.value = err.message || 'Error al eliminar comentario'
  }
}

// Métodos auxiliares para formateo
const formatDate = (dateString: string | undefined | null) => {
  if (!dateString) {
    console.warn('⚠️ formatDate recibió fecha inválida:', dateString)
    return 'Fecha no disponible'
  }
  
  const date = new Date(dateString)
  
  // Validar que la fecha es válida
  if (isNaN(date.getTime())) {
    console.warn('⚠️ formatDate: fecha inválida tras parseo:', dateString)
    return 'Fecha inválida'
  }
  
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  
  if (diffMins < 1) {
    return 'Ahora mismo'
  } else if (diffMins < 60) {
    return `Hace ${diffMins} minuto${diffMins !== 1 ? 's' : ''}`
  } else if (diffHours < 24) {
    return `Hace ${diffHours} hora${diffHours !== 1 ? 's' : ''}`
  } else if (diffDays < 7) {
    return `Hace ${diffDays} día${diffDays !== 1 ? 's' : ''}`
  } else {
    return date.toLocaleDateString('es-DO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }
}

const getPriorityColor = (priority: string) => {
  const colors: Record<string, string> = {
    CRITICAL: 'error',
    HIGH: 'warning',
    MEDIUM: 'info',
    LOW: 'success'
  }
  return colors[priority] || 'grey'
}

const getPriorityText = (priority: string) => {
  const texts: Record<string, string> = {
    CRITICAL: 'Crítica',
    HIGH: 'Alta',
    MEDIUM: 'Media',
    LOW: 'Baja'
  }
  return texts[priority] || priority
}

const getStatusColor = (status: string) => {
  const colors: Record<string, string> = {
    PENDING: 'warning',
    IN_PROGRESS: 'info',
    PENDING_VALIDATION: 'purple',
    CLOSED: 'grey',
    CANCELLED: 'error'
  }
  return colors[status] || 'grey'
}

const getStatusText = (status: string) => {
  const texts: Record<string, string> = {
    PENDING: 'Pendiente',
    IN_PROGRESS: 'En Proceso',
    PENDING_VALIDATION: 'Pendiente Validación',
    CLOSED: 'Cerrado',
    CANCELLED: 'Cancelado'
  }
  return texts[status] || status
}

// WebSocket listeners para tiempo real.
//
// P13-K4A: these events used to arrive through the `incident:{id}` room, so room membership itself
// guaranteed a payload belonged to the open incidence. Delivery is now addressed to `user:{id}` and
// carries every incidence this actor is an eligible recipient of, so each handler must check the
// subject. Without this a comment on incidence B would render inside incidence A's thread.
const belongsToOpenIncident = (incidentId: unknown): boolean => {
  if (!props.incident) return false
  return Number(incidentId) === Number(props.incident.id)
}

const handleCommentCreated = (newComment: Comment & { incidentId?: number }) => {
  console.log('📨 Nuevo comentario recibido via WebSocket:', newComment)
  if (!belongsToOpenIncident(newComment?.incidentId)) return
  // Solo agregar si no es el comentario que yo acabo de crear
  if (newComment.userId !== auth.user?.id) {
    comments.value.push(newComment)
  }
}

const handleCommentUpdated = (updatedComment: Comment & { incidentId?: number }) => {
  console.log('✏️ Comentario editado recibido via WebSocket:', updatedComment)
  if (!belongsToOpenIncident(updatedComment?.incidentId)) return
  const index = comments.value.findIndex(c => c.id === updatedComment.id)
  if (index !== -1 && updatedComment.userId !== auth.user?.id) {
    comments.value[index] = updatedComment
  }
}

const handleCommentDeleted = (data: { id: string, incidentId: number, userId: number }) => {
  console.log('🗑️ Comentario eliminado recibido via WebSocket:', data)
  if (!belongsToOpenIncident(data?.incidentId)) return
  // Recargar todos los comentarios para mostrar mensaje de eliminación
  if (data.userId !== auth.user?.id) {
    loadComments()
  }
}

const setupWebSocketListeners = () => {
  if (!props.incident) return

  websocketService.on(RT_LOCAL.COMMENT_CREATED, handleCommentCreated)
  websocketService.on(RT_LOCAL.COMMENT_UPDATED, handleCommentUpdated)
  websocketService.on(RT_LOCAL.COMMENT_DELETED, handleCommentDeleted)
}

const cleanupWebSocketListeners = () => {
  if (!props.incident) return

  // Remover listeners CON referencia de handler (no destruir listeners de otros componentes)
  websocketService.off(RT_LOCAL.COMMENT_CREATED, handleCommentCreated)
  websocketService.off(RT_LOCAL.COMMENT_UPDATED, handleCommentUpdated)
  websocketService.off(RT_LOCAL.COMMENT_DELETED, handleCommentDeleted)
}

// Watchers
watch(() => props.modelValue, (newValue) => {
  if (newValue && props.incident) {
    loadComments()
    setupWebSocketListeners()
    newComment.value = ''
    nextTick(() => {
      formRef.value?.resetValidation()
    })
  } else {
    cleanupWebSocketListeners()
  }
})
</script>

<style scoped>
.comments-dialog {
  border-radius: 12px !important;
  overflow: hidden;
}

.dialog-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
  padding: 1.5rem 2rem;
  position: sticky;
  top: 0;
  z-index: 10;
}

.dialog-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #212529;
  margin: 0;
}

.dialog-subtitle {
  color: #6c757d;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

/* Información de la incidencia */
.incident-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.incident-title {
  font-size: 1rem;
}

.incident-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* Contenedor de comentarios */
.comments-container {
  min-height: 400px;
}

.comments-list {
  background: #fafafa;
}

.no-comments {
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Timeline de comentarios */
.comments-timeline {
  padding: 1rem 0;
}

.comment-card {
  margin-bottom: 1rem;
  width: 100%;
}

.comment-item {
  border-radius: 12px !important;
  transition: all 0.2s ease;
}

.comment-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Estilo para mensajes eliminados (WhatsApp style) */
.deleted-comment {
  background-color: #f5f5f5 !important;
  border: 1px dashed #d0d0d0 !important;
}

.deleted-text {
  font-style: italic;
  color: #999 !important;
}

/* Preview de respuesta */
.reply-to-preview {
  background: #f0f0f0;
  border-left: 3px solid #2196F3;
  padding: 0.5rem;
  border-radius: 4px;
  font-size: 0.85rem;
  color: #666;
}

/* Badge de editado */
.edited-badge {
  font-size: 0.75rem;
  color: #999;
  font-style: italic;
}

.comment-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.comment-author {
  display: flex;
  align-items: center;
}

.author-name {
  font-weight: 600;
  color: #333;
  font-size: 0.95rem;
}

.comment-time {
  font-size: 0.8rem;
  color: #666;
  margin-top: 0.25rem;
}

.comment-content {
  font-size: 0.95rem;
  line-height: 1.6;
  color: #444;
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border-left: 3px solid #2196f3;
}

/* Formulario de comentarios */
.comment-form-container {
  background: white;
  border-top: 1px solid #dee2e6;
  position: sticky;
  bottom: 0;
  z-index: 10;
}

.comment-tips {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
}

.comment-actions {
  display: flex;
  align-items: center;
}

/* Mejoras en los campos del formulario */
:deep(.v-field--variant-outlined) {
  border-radius: 8px;
}

:deep(.v-textarea .v-field__input) {
  line-height: 1.5;
}

/* Estados de validación */
:deep(.v-text-field--error .v-field__outline) {
  border-color: #dc3545 !important;
  border-width: 2px !important;
}

/* Timeline personalizado */
:deep(.v-timeline-item__body) {
  width: 100% !important;
  max-width: none !important;
}

:deep(.v-timeline-item__opposite) {
  display: none !important;
}

/* Responsive */
@media (max-width: 768px) {
  .dialog-header {
    padding: 1rem;
  }
  
  .dialog-title {
    font-size: 1.2rem;
  }
  
  .comment-form-container {
    padding: 1rem !important;
  }
  
  .comment-actions {
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }
  
  .comment-actions .v-btn {
    width: 100%;
    margin: 0 !important;
  }
  
  .comment-tips {
    margin-bottom: 1rem;
    justify-content: center;
  }
  
  .incident-meta {
    justify-content: center;
  }
}

/* Animaciones */
.comment-card {
  animation: slideInUp 0.3s ease-out;
}

@keyframes slideInUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Scrollbar personalizado */
.comments-container::-webkit-scrollbar {
  width: 6px;
}

.comments-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.comments-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.comments-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Mejoras de accesibilidad */
:deep(.v-btn:focus-visible) {
  outline: 2px solid #0d6efd;
  outline-offset: 2px;
}

:deep(.v-field:focus-within) {
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.25);
}
</style>
